import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  Collapse,
} from '@mui/material';
import CloseIcon from 'mdi-react/CloseIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useGetExecutionQuery } from 'store/app/entities/BatchSlice';
import { I18n } from 'utils/i18n';

// Helper to safely extract batchConfigId value (handles both string and object)
const getBatchConfigIdLabel = batchConfigId => {
  if (!batchConfigId) return null;
  if (typeof batchConfigId === 'string') return batchConfigId;
  return batchConfigId._id || batchConfigId.name || 'Batch Config';
};

const statusColorMap = {
  running: 'primary',
  success: 'success',
  failure: 'error',
  timeout: 'warning',
  pending: 'default',
};

const JobDetailsDrawer = ({ execution, isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({
    result: false,
    error: false,
  });
  const executionId = execution?._id || execution?.executionId;
  const {
    data: executionData,
    isLoading,
    refetch,
  } = useGetExecutionQuery(executionId, { skip: !executionId });

  const exec = executionData?.execution || execution;

  useEffect(() => {
    if (isOpen && executionId) {
      refetch();
    }
  }, [isOpen, executionId, refetch]);

  const toggleSection = section => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (!exec) return null;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 600 } } }}
    >
      <Box sx={{ padding: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
          }}
        >
          <Typography variant="h5">
            {I18n.t('AdminBatchScene.ExecutionDetails') || 'Execution Details'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ marginBottom: 2 }} />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {I18n.t('AdminBatchScene.JobName') || 'Job Name'}
                </Typography>
                <Typography variant="h6">{exec.jobName}</Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="subtitle2" color="textSecondary">
                    {I18n.t('AdminBatchScene.Status') || 'Status'}
                  </Typography>
                  <Chip
                    label={exec.status}
                    color={statusColorMap[exec.status] || 'default'}
                    size="small"
                    variant="soft"
                  />
                </Box>
              </CardContent>
            </Card>

            {exec.startedAt && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {I18n.t('AdminBatchScene.StartedAt') || 'Started At'}
                  </Typography>
                  <Typography>{moment(exec.startedAt).format('YYYY-MM-DD HH:mm:ss')}</Typography>
                </CardContent>
              </Card>
            )}

            {exec.completedAt && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {I18n.t('AdminBatchScene.CompletedAt') || 'Completed At'}
                  </Typography>
                  <Typography>{moment(exec.completedAt).format('YYYY-MM-DD HH:mm:ss')}</Typography>
                </CardContent>
              </Card>
            )}

            {exec.duration && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {I18n.t('AdminBatchScene.Duration') || 'Duration'}
                  </Typography>
                  <Typography>{(exec.duration / 1000).toFixed(2)}s</Typography>
                </CardContent>
              </Card>
            )}

            {exec.workerId && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {I18n.t('AdminBatchScene.WorkerId') || 'Worker ID'}
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {exec.workerId}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {exec.batchConfigId && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {I18n.t('AdminBatchScene.BatchConfig') || 'Batch Config ID'}
                  </Typography>
                  <Chip
                    label={getBatchConfigIdLabel(exec.batchConfigId)}
                    size="small"
                    variant="outlined"
                  />
                </CardContent>
              </Card>
            )}

            {exec.result && (
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleSection('result')}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {I18n.t('AdminBatchScene.Result') || 'Result'}
                    </Typography>
                    {expandedSections.result ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </Box>
                  <Collapse in={expandedSections.result}>
                    <Box
                      sx={{
                        marginTop: 2,
                        padding: 2,
                        backgroundColor: 'background.default',
                        borderRadius: 1,
                        maxHeight: 400,
                        overflow: 'auto',
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                        {JSON.stringify(exec.result, null, 2)}
                      </pre>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            )}

            {exec.error && (
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleSection('error')}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {I18n.t('AdminBatchScene.Error') || 'Error'}
                    </Typography>
                    {expandedSections.error ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </Box>
                  <Collapse in={expandedSections.error}>
                    <Box
                      sx={{
                        marginTop: 2,
                        padding: 2,
                        backgroundColor: 'error.light',
                        borderRadius: 1,
                        maxHeight: 400,
                        overflow: 'auto',
                      }}
                    >
                      {exec.error.name && (
                        <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                          {exec.error.name}
                        </Typography>
                      )}
                      {exec.error.message && (
                        <Typography variant="body2" sx={{ marginBottom: 2 }}>
                          {exec.error.message}
                        </Typography>
                      )}
                      {exec.error.stack && (
                        <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                          {exec.error.stack}
                        </pre>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default JobDetailsDrawer;
